import type { ColumnDef } from '@tanstack/react-table';
import { Link } from '@tanstack/react-router';
import { Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { DataTableColumnHeader } from '@/components/common/data-table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { formatCategory } from '@/lib/constants/integration-categories';
import type { TenantIntegrationInstance, IntegrationDefinition } from '@/hooks/use-integrations';
import type { TenantRead } from '@harmonyso/tenants-sdk';

export interface IntegrationsTableMeta {
  tenants?: TenantRead[];
  definitions?: IntegrationDefinition[];
}

export const createIntegrationColumns = (
  tenants?: TenantRead[],
  definitions?: IntegrationDefinition[],
): ColumnDef<TenantIntegrationInstance>[] => [
  {
    size: 180,
    accessorKey: 'tenant_id',
    enableColumnFilter: true,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Tenant" />,
    cell: ({ row }) => {
      const tenantId = row.original.tenant_id;
      const tenant = tenants?.find(t => t.id === tenantId);
      const tenantName = tenant?.company || tenantId.slice(0, 8);
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to="/tenants/$tenantId"
              params={{ tenantId }}
              className="text-sm font-medium text-foreground hover:text-primary hover:underline truncate block"
            >
              {tenantName}
            </Link>
          </TooltipTrigger>
          <TooltipContent>
            <p>{tenantId}</p>
          </TooltipContent>
        </Tooltip>
      );
    },
    meta: {
      displayName: 'Tenant',
      getColumnFilters: ({ table }) => {
        const meta = table.options.meta as IntegrationsTableMeta | undefined;
        const t = meta?.tenants;
        if (!t) return [];
        return t.map(tenant => ({
          label: tenant.company || tenant.id.slice(0, 8),
          value: tenant.id,
        }));
      },
    },
  },
  {
    size: 160,
    accessorKey: 'integration_id',
    enableColumnFilter: true,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Integration" />,
    cell: ({ row }) => {
      const def = definitions?.find(d => d.id === row.original.integration_id);
      const name = def?.name ?? row.original.integration_id;
      // ITSM integrations deep-link to their ITSM Config (exact credential).
      if (def?.category_id === 'itsm') {
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/itsm-config"
                search={{
                  tenantId: row.original.tenant_id,
                  credentialId: row.original.id,
                }}
                className="flex items-center gap-1 text-sm font-medium text-foreground hover:text-primary hover:underline truncate"
              >
                <span className="truncate">{name}</span>
                <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
              </Link>
            </TooltipTrigger>
            <TooltipContent>Open in ITSM Config</TooltipContent>
          </Tooltip>
        );
      }
      return (
        <div className="text-sm font-medium text-foreground truncate">
          {name}
        </div>
      );
    },
    meta: {
      displayName: 'Integration',
      getColumnFilters: ({ table }) => {
        const meta = table.options.meta as IntegrationsTableMeta | undefined;
        const defs = meta?.definitions;
        if (!defs) return [];
        return defs
          .filter(d => !d.deprecated)
          .map(d => ({ label: d.name, value: d.id }))
          .sort((a, b) => a.label.localeCompare(b.label));
      },
    },
  },
  {
    size: 180,
    accessorKey: 'name',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Instance" />,
    cell: ({ row }) => {
      const displayName = row.original.label || row.original.name;
      const def = definitions?.find(d => d.id === row.original.integration_id);
      const isOpaque = /^[0-9a-f]{8}-[0-9a-f]{4}/.test(displayName) || /^T[A-Z0-9]{9,}$/.test(displayName);
      return (
        <div className="text-sm text-foreground truncate">
          {isOpaque ? (def?.name ?? displayName) : displayName}
        </div>
      );
    },
  },
  {
    size: 130,
    id: 'category',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Category" />,
    cell: ({ row }) => {
      const def = definitions?.find(d => d.id === row.original.integration_id);
      const category = def?.category_id ?? '—';
      return (
        <Badge variant="secondary">
          {formatCategory(category)}
        </Badge>
      );
    },
  },
  {
    size: 100,
    accessorKey: 'deprecated',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }) => (
      <Badge variant={row.original.deprecated ? 'destructive' : 'default'}>
        {row.original.deprecated ? 'Deprecated' : 'Active'}
      </Badge>
    ),
  },
  {
    id: 'copy_id',
    size: 50,
    enableSorting: false,
    enableHiding: false,
    header: () => null,
    cell: ({ row }) => (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="flex items-center justify-center cursor-pointer hover:opacity-70 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              void navigator.clipboard.writeText(row.original.id);
              toast.success('Instance ID copied');
            }}
          >
            <Copy className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Copy instance ID</TooltipContent>
      </Tooltip>
    ),
  },
];
